import axios from 'axios'
import Flex from 'components/common/Flex'
// import moment from 'moment'
import moment from "moment-timezone";
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Card, Col, Row, Badge, Spinner, Form } from 'react-bootstrap'

const STALE_MS = 15 * 60 * 1000
const LS_KEY_DATA = 'cycleStatus:data'
const LS_KEY_TS = 'cycleStatus:ts'

const CycleStatus = ({ facilityFlag }) => {
  const [masterItems, setMasterItems] = useState([]);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  let facilitiesData = localStorage.getItem("facilitiesData") ? JSON.parse(localStorage.getItem("facilitiesData")) : [];

  const readCache = () => {
    try {
      const raw = localStorage.getItem(LS_KEY_DATA)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }

  const readTs = () => {
    const n = Number(localStorage.getItem(LS_KEY_TS))
    return Number.isFinite(n) ? n : 0
  }

  const isStale = ts => !ts || Date.now() - ts >= STALE_MS

  const saveCache = (data) => {
    localStorage.setItem(LS_KEY_DATA, JSON.stringify(data))
    localStorage.setItem(LS_KEY_TS, String(Date.now()))
  }

  const applyFilter = (which, list) => {
    if (which === 'pending') return list.filter(i => i.census_status === 0)
    if (which === 'completed') return list.filter(i => i.census_status === 1)
    return list
  }

  const fetchData = useCallback(async () => {
    try {
      setError(null)
      const tokenStr = localStorage.getItem('accessToken')
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/cycles/cycle-status`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      )
      const list = Array.isArray(data?.cycleData) ? data.cycleData : []
      setMasterItems(list)
      setItems(applyFilter(filter, list))
      saveCache(list)
    } catch {
      setError('Unable to load cycle status')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    const cached = readCache()
    const ts = readTs()

    if (cached && !isStale(ts)) {
      setMasterItems(cached)
      setItems(applyFilter(filter, cached))
      setLoading(false)
    } else {
      fetchData()
    }

    const onFocus = () => {
      const tsNow = readTs()
      if (isStale(tsNow)) {
        setLoading(true)
        fetchData()
      }
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)

    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [fetchData, facilityFlag])

  useEffect(() => {
    setItems(applyFilter(filter, masterItems))
  }, [filter, masterItems])

  const getBadge = d => {
    if (d == 1) return { text: `${d} day`, bg: 'danger' }
    if (d <= 3) return { text: `${d} days`, bg: 'danger' }
    if (d <= 7) return { text: `${d} days`, bg: 'warning' }
    if (d <= 14) return { text: `${d} days`, bg: 'info' }
    return { text: `${d} days`, bg: 'success' }
  }

  const total = useMemo(() => masterItems.length, [masterItems])
  const pendingCount = useMemo(
    () => masterItems.reduce((acc, i) => acc + (i.census_status === 0 ? 1 : 0), 0),
    [masterItems]
  )

  return (
    <Card className="h-100">
      <Card.Body className="pt-2">
        <Row className="g-0 h-100 align-items-start">
          <Col as={Flex} direction="column" className="w-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Cycle Status</h6>
              <div className="d-flex align-items-center gap-3">
                <Form.Check
                  inline
                  type="radio"
                  id="filter-all"
                  name="cycle-filter"
                  label="All"
                  value="all"
                  checked={filter === 'all'}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="filter-pending"
                  name="cycle-filter"
                  label="Pending"
                  value="pending"
                  checked={filter === 'pending'}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="filter-completed"
                  name="cycle-filter"
                  label="Completed"
                  value="completed"
                  checked={filter === 'completed'}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>

              <div className="d-flex align-items-end gap-4">
                <div className="text-end">
                  <div className="text-700 small">Total</div>
                  <div className="fw-bold" style={{ fontSize: '1.75rem', lineHeight: 1 }}>
                    {total}
                  </div>
                </div>
                <div className="text-end">
                  <div className="text-700 small">Pending</div>
                  <div
                    className="fw-bold text-primary"
                    style={{ fontSize: '1.75rem', lineHeight: 1 }}
                  >
                    {pendingCount}
                  </div>
                </div>
              </div>
            </div>
            <div
              className="border rounded-3 p-2"
              style={{
                maxHeight: 320,
                overflowY: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
            >
              <style>{`.border::-webkit-scrollbar{display:none}`}</style>

              {loading && (
                <div className="d-flex align-items-center justify-content-center py-5">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span className="fs-10">Loading</span>
                </div>
              )}

              {error && !loading && (
                <div className="text-danger small py-2">{error}</div>
              )}

              {!loading && !error && items.length === 0 && (
                <div className="text-700 small py-2">No cycles found</div>
              )}

              {!loading && !error && items.map(item => {
                const badge = getBadge(item.days_left)
                return (
                  <div
                    key={item.id}
                    className="d-flex align-items-center justify-content-between py-2 px-2 mb-2 rounded-2 bg-100"
                  >
                    <div className="d-flex flex-column">
                      <div className="fw-semibold">
                        {facilitiesData.find(
                          f => Number(f.value) === Number(item.facility_id)
                        )?.key}
                      </div>
                      {item.census_status !== 1 && (
                        <div className="small text-700">
                          Before {moment(item.census_before, "YYYY-MM-DD", "America/New_York").format("MM/DD/YY")}
                          {/* {moment(item.census_before).format("MM/DD/YY")} */}
                        </div>
                      )}
                      {item.census_status === 1 ? (
                        <div className="small text-700">
                          Completed on {moment(item.census_completed_on).format("MM/DD/YY hh:mm A")} by {item.census_completed_by_username}
                        </div>
                      ) : (
                        <div className="small text-500">Status Pending</div>
                      )}
                    </div>
                    {item.census_status !== 1 ? (
                      <div className="text-end">
                        <span
                          className={`badge bg-${badge.bg} mb-1`}
                          style={{ fontSize: '.85rem' }}
                        >
                          {badge.text}
                        </span>
                      </div>
                    ) : (
                      <div className="text-end">
                        <span
                          className="badge bg-success mb-1"
                          style={{ fontSize: '.85rem' }}
                        >
                          Completed
                        </span>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

export default CycleStatus